import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { LOCATIONS } from "@/lib/suppliers";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  AlertTriangle, Receipt, Wallet, Plus, Trash2, TrendingDown, TrendingUp, Eye,
} from "lucide-react";
import ImageLightbox from "@/components/ImageLightbox";
import { useT } from "@/lib/i18n";

const OPEX_KINDS = [
  { value: "rent",        label: "Rent · Ενοίκιο" },
  { value: "electricity", label: "Electricity / AHK · ΑΗΚ" },
  { value: "payroll",     label: "Payroll · Μισθοδοσία" },
  { value: "municipal",   label: "Municipal Tax · Δημοτικά" },
  { value: "cashflow_expense", label: "Daily Cashflow Expense · Καθημερινό Έξοδο" },
  { value: "other",       label: "Other · Άλλο" },
];

function monthKey(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function PortalExtras({ onOpenFolder }) {
  const t = useT();
  const [alerts, setAlerts] = useState([]);
  const [debts, setDebts] = useState({ rows: [], total_outstanding: 0 });
  // Per-supplier image gallery (Visual Media Audit Vault)
  const [gallerySupplier, setGallerySupplier] = useState(null);
  const [galleryInvoices, setGalleryInvoices] = useState([]);
  const [lightbox, setLightbox] = useState({ src: null, caption: "" });

  const openGallery = async (supplier) => {
    setGallerySupplier(supplier);
    setGalleryInvoices([]);
    try {
      const { data } = await api.get("/portal/supplier-invoices", { params: { supplier } });
      setGalleryInvoices(data || []);
    } catch {
      toast.error("Could not load supplier invoices");
    }
  };
  const [pnl, setPnl] = useState(null);
  const [opex, setOpex] = useState([]);
  const [month, setMonth] = useState(monthKey());
  const [opexForm, setOpexForm] = useState({
    location: "Aglantzia", kind: "rent", amount: "", note: "",
  });

  const load = async () => {
    try {
      const [a, d, p, o] = await Promise.all([
        api.get("/portal/alerts"),
        api.get("/portal/supplier-debts"),
        api.get(`/portal/pnl?month=${month}`),
        api.get(`/portal/opex?month=${month}`),
      ]);
      setAlerts(a.data.alerts);
      setDebts(d.data);
      setPnl(p.data);
      setOpex(o.data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => { load(); }, [month]); // eslint-disable-line

  const addOpex = async () => {
    if (!opexForm.amount || Number(opexForm.amount) <= 0) {
      toast.error("Amount required"); return;
    }
    try {
      await api.post("/portal/opex", {
        location: opexForm.location,
        month,
        kind: opexForm.kind,
        amount: Number(opexForm.amount),
        note: opexForm.note,
      });
      toast.success("OpEx entry saved");
      setOpexForm((p) => ({ ...p, amount: "", note: "" }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const removeOpex = async (id) => {
    try { await api.delete(`/portal/opex/${id}`); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="mt-8 space-y-6" data-testid="portal-extras">
      {/* === ALERTS STREAM (top of extras) === */}
      <Card className="p-4 border-2 border-rose-200 bg-rose-50/40" data-testid="alerts-feed">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="h-5 w-5 text-rose-700" />
          <h3 className="font-bold text-slate-900">AI Discrepancy & Operational Alerts</h3>
          <span className="ml-2 text-[10px] font-mono text-slate-500">
            threshold ≤ −€15 · last 14 days · {alerts.length} alert(s)
          </span>
        </div>
        {alerts.length === 0 ? (
          <div className="px-3 py-3 text-center text-xs text-slate-400 italic border border-dashed border-rose-200 rounded-lg">
            No alerts — all kiosks within tolerance.
          </div>
        ) : (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {alerts.map((a) => (
              <button
                key={a.id}
                data-testid={`alert-${a.id}`}
                onClick={() => onOpenFolder?.(a.target_folder?.store, a.target_folder?.date)}
                className={`shrink-0 text-left px-3 py-2 rounded-xl border-2 min-w-[280px] max-w-[360px] ${
                  a.severity === "high"
                    ? "border-rose-300 bg-rose-100 hover:bg-rose-200"
                    : "border-amber-300 bg-amber-100 hover:bg-amber-200"
                }`}
              >
                <div className="text-[10px] uppercase tracking-widest font-bold text-rose-700">
                  {a.kind} · {a.location} · {a.date}
                </div>
                <div className="text-sm text-slate-900 truncate font-semibold">{a.message}</div>
                <div className="text-[10px] font-mono text-slate-500">Tap to open daily folder →</div>
              </button>
            ))}
          </div>
        )}
      </Card>

      {/* === SUPPLIER DEBT MATRIX === */}
      <Card className="p-5" data-testid="supplier-debts">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-slate-700" />
            <h3 className="font-bold text-slate-900" data-testid="supplier-debt-title">{t("common.supplierDebtMatrix")}</h3>
          </div>
          <div className="font-mono text-sm font-bold text-rose-700" data-testid="debts-total">
            Total Outstanding: €{debts.total_outstanding.toFixed(2)}
          </div>
        </div>
        {debts.rows.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-slate-400 italic border border-dashed border-slate-200 rounded-lg">
            No outstanding invoices.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 font-bold border-b border-slate-200">
                  <th className="py-2 pr-3">Supplier</th>
                  <th className="py-2 pr-3 text-right">Invoices</th>
                  <th className="py-2 pr-3">Oldest</th>
                  <th className="py-2 pr-3 text-right">Outstanding</th>
                  <th className="py-2 pr-3 text-center w-10">Vault</th>
                </tr>
              </thead>
              <tbody>
                {debts.rows.map((d) => (
                  <tr key={d.supplier_name} className="border-b border-slate-100" data-testid={`debt-row-${d.supplier_name.replace(/\W+/g, "-").toLowerCase()}`}>
                    <td className="py-2 pr-3 font-semibold text-slate-900">{d.supplier_name}</td>
                    <td className="py-2 pr-3 text-right font-mono">{d.invoice_count}</td>
                    <td className="py-2 pr-3 text-xs font-mono text-slate-500">{d.oldest_issue || "—"}</td>
                    <td className="py-2 pr-3 text-right font-mono font-bold text-rose-700">€{d.outstanding.toFixed(2)}</td>
                    <td className="py-2 pr-3 text-center">
                      <button
                        onClick={() => openGallery(d.supplier_name)}
                        className="h-8 w-8 rounded-md text-slate-500 hover:text-white hover:bg-slate-900 inline-flex items-center justify-center transition-colors"
                        title="View receipt photos"
                        data-testid={`debt-vault-${d.supplier_name.replace(/\W+/g, "-").toLowerCase()}`}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* === OPEX + P&L === */}
      <Card className="p-5" data-testid="opex-pnl">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-slate-700" />
            <h3 className="font-bold text-slate-900">OpEx & Net Profit · Λειτουργικά + Καθαρό Κέρδος</h3>
          </div>
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="pnl-month"
            className="w-44 min-h-[40px]"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* P&L summary */}
          <div className="grid grid-cols-3 gap-3" data-testid="pnl-summary">
            <Metric label="Gross Revenue" value={`€${(pnl?.gross_revenue || 0).toFixed(2)}`} accent="text-emerald-700" icon={<TrendingUp className="h-4 w-4" />} />
            <Metric label="OpEx" value={`€${(pnl?.opex_total || 0).toFixed(2)}`} accent="text-orange-700" icon={<TrendingDown className="h-4 w-4" />} />
            <Metric
              label="Net Profit"
              value={`${(pnl?.net_profit || 0) >= 0 ? "+" : ""}€${(pnl?.net_profit || 0).toFixed(2)}`}
              accent={(pnl?.net_profit || 0) >= 0 ? "text-emerald-700" : "text-rose-700"}
            />
          </div>

          {/* OpEx entry form */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="opex-form">
            <div className="grid grid-cols-2 gap-2 mb-2">
              <Select value={opexForm.location} onValueChange={(v) => setOpexForm({ ...opexForm, location: v })}>
                <SelectTrigger className="min-h-[40px]" data-testid="opex-location"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={opexForm.kind} onValueChange={(v) => setOpexForm({ ...opexForm, kind: v })}>
                <SelectTrigger className="min-h-[40px]" data-testid="opex-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OPEX_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Input
                type="number" step="0.01" min="0"
                value={opexForm.amount}
                onChange={(e) => setOpexForm({ ...opexForm, amount: e.target.value })}
                placeholder="€0.00"
                data-testid="opex-amount"
                className="min-h-[40px] font-mono text-center font-bold col-span-1"
              />
              <Input
                value={opexForm.note}
                onChange={(e) => setOpexForm({ ...opexForm, note: e.target.value })}
                placeholder="Note (optional)"
                data-testid="opex-note"
                className="min-h-[40px] col-span-1"
              />
              <Button onClick={addOpex} data-testid="opex-add-btn" className="min-h-[40px] gap-1 bg-slate-900 hover:bg-slate-800">
                <Plus className="h-4 w-4" /> Add
              </Button>
            </div>

            {opex.length > 0 && (
              <div className="mt-3 space-y-1 max-h-[160px] overflow-y-auto" data-testid="opex-list">
                {opex.map((o) => (
                  <div key={o.id} className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-md px-2 py-1 text-xs">
                    <span className="font-mono text-slate-600 uppercase w-20 shrink-0">{o.kind}</span>
                    <span className="text-slate-700 flex-1 truncate">{o.location} · {o.note || "—"}</span>
                    <span className="font-mono font-bold text-slate-900">€{Number(o.amount).toFixed(2)}</span>
                    <button onClick={() => removeOpex(o.id)} className="p-1 rounded hover:bg-rose-100 text-rose-600" data-testid={`opex-del-${o.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Per-supplier image gallery (Visual Media Audit Vault) */}
      <Dialog
        open={!!gallerySupplier}
        onOpenChange={(o) => !o && setGallerySupplier(null)}
      >
        <DialogContent className="max-w-4xl" data-testid="supplier-gallery">
          <DialogTitle>{gallerySupplier} · Receipts & Cheques</DialogTitle>
          {galleryInvoices.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center" data-testid="supplier-gallery-empty">
              No open invoices for this supplier.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[65vh] overflow-y-auto pr-1">
              {galleryInvoices.map((inv) => (
                <button
                  key={inv.id}
                  onClick={() => setLightbox({
                    src: inv.invoice_image_url,
                    caption: `${inv.supplier_name} · ${inv.invoice_number || "—"} · ${inv.issue_date || ""} · €${(inv.total_amount || 0).toFixed(2)}`,
                  })}
                  disabled={!inv.invoice_image_url}
                  className="relative group bg-slate-50 border border-slate-200 rounded-lg overflow-hidden text-left disabled:opacity-40"
                  data-testid={`gallery-item-${inv.id}`}
                >
                  {inv.invoice_image_url ? (
                    <img src={fileUrl(inv.invoice_image_url)} alt="" className="w-full h-32 object-cover" />
                  ) : (
                    <div className="w-full h-32 flex items-center justify-center text-slate-400 text-xs">No image</div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <Eye className="h-6 w-6" />
                  </div>
                  <div className="p-2 text-[11px] font-mono leading-tight">
                    <div className="truncate font-bold text-slate-900">€{(inv.total_amount || 0).toFixed(2)}</div>
                    <div className="text-slate-500 truncate">{inv.issue_date || "—"} · {inv.location}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ImageLightbox
        open={!!lightbox.src}
        src={lightbox.src}
        caption={lightbox.caption}
        onClose={() => setLightbox({ src: null, caption: "" })}
      />
    </div>
  );
}

function Metric({ label, value, accent, icon }) {
  return (
    <div className="bg-white border-2 border-slate-200 rounded-xl px-3 py-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
        {icon} {label}
      </div>
      <div className={`font-mono text-xl font-bold mt-1 ${accent || "text-slate-900"}`}>{value}</div>
    </div>
  );
}
